/**
 * Validation utilities for dataset creation
 */

import type { DatasetValidationError, PartitionField, MetadataField } from '../types/dataset.types';

/**
 * Validate dataset name
 */
export function validateDatasetName(name: string): DatasetValidationError[] {
  const errors: DatasetValidationError[] = [];

  if (!name || name.trim().length === 0) {
    errors.push({
      field: 'name',
      message: 'Dataset name is required',
      code: 'NAME_REQUIRED',
    });
    return errors;
  }

  if (name.length > 255) {
    errors.push({
      field: 'name',
      message: 'Dataset name must not exceed 255 characters',
      code: 'NAME_TOO_LONG',
    });
  }

  // Validate naming pattern: must start with a letter, only letters, numbers, and underscores
  if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(name)) {
    errors.push({
      field: 'name',
      message: 'Dataset name must start with a letter and contain only letters, numbers, and underscores (no hyphens or spaces)',
      code: 'NAME_INVALID_PATTERN',
    });
  }

  return errors;
}

/**
 * Validate source (optional field)
 * Source can be null - no validation errors if not provided
 */
export function validateSource(_sourceId: string | null): DatasetValidationError[] {
  // Source is optional, no errors if null
  return [];
}

/**
 * Validate partitions - AT LEAST 1 REQUIRED
 */
export function validatePartitions(partitions: PartitionField[]): DatasetValidationError[] {
  const errors: DatasetValidationError[] = [];

  // REQUIRED: At least 1 partition
  if (partitions.length === 0) {
    errors.push({
      field: 'partitions',
      message: 'At least one partition is required',
      code: 'PARTITIONS_REQUIRED',
    });
    return errors;
  }

  // Check for duplicates
  const names = partitions.map((p) => p.name.toLowerCase());
  const duplicates = names.filter((name, index) => names.indexOf(name) !== index);
  if (duplicates.length > 0) {
    errors.push({
      field: 'partitions',
      message: `Duplicate partition names: ${[...new Set(duplicates)].join(', ')}`,
      code: 'PARTITIONS_DUPLICATE',
    });
  }

  // Validate each partition name
  partitions.forEach((p, index) => {
    if (!p.name || p.name.trim().length === 0) {
      errors.push({
        field: `partitions[${index}]`,
        message: 'Partition name cannot be empty',
        code: 'PARTITION_NAME_EMPTY',
      });
    } else if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(p.name)) { // nosemgrep: rules.lgpl.nodejs_scan.javascript-dos-rule-regex_dos  -- anchored linear identifier regex, no backtracking
      errors.push({
        field: `partitions[${index}]`,
        message: `Invalid partition name "${p.name}": must start with a letter and contain only letters, numbers, and underscores (no hyphens or spaces)`,
        code: 'PARTITION_NAME_INVALID',
      });
    }
  });

  return errors;
}

/**
 * Validate metadata - OPTIONAL but if present must be valid
 */
export function validateMetadata(metadata: MetadataField[]): DatasetValidationError[] {
  const errors: DatasetValidationError[] = [];

  // Max 50 entries
  if (metadata.length > 50) {
    errors.push({
      field: 'metadata',
      message: 'Maximum 50 metadata entries allowed',
      code: 'METADATA_TOO_MANY',
    });
  }

  // Check for duplicate keys
  const keys = metadata.map((m) => m.key.toLowerCase());
  const duplicates = keys.filter((key, index) => keys.indexOf(key) !== index);
  if (duplicates.length > 0) {
    errors.push({
      field: 'metadata',
      message: `Duplicate metadata keys: ${[...new Set(duplicates)].join(', ')}`,
      code: 'METADATA_DUPLICATE',
    });
  }

  // Validate each entry
  metadata.forEach((m, index) => {
    if (!m.key || m.key.trim().length === 0) {
      errors.push({
        field: `metadata[${index}]`,
        message: 'Metadata key cannot be empty',
        code: 'METADATA_KEY_EMPTY',
      });
    } else if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(m.key)) { // nosemgrep: rules.lgpl.nodejs_scan.javascript-dos-rule-regex_dos  -- anchored linear identifier regex, no backtracking
      errors.push({
        field: `metadata[${index}]`,
        message: `Invalid metadata key "${m.key}": must start with a letter and contain only letters, numbers, and underscores (no hyphens or spaces)`,
        code: 'METADATA_KEY_INVALID',
      });
    }

    if (!m.value || m.value.trim().length === 0) {
      errors.push({
        field: `metadata[${index}]`,
        message: 'Metadata value cannot be empty',
        code: 'METADATA_VALUE_EMPTY',
      });
    }
  });

  // Size check (approximate - 100KB limit)
  try {
    const totalSize = JSON.stringify(metadata).length;
    if (totalSize > 100000) {
      errors.push({
        field: 'metadata',
        message: 'Total metadata size exceeds 100KB limit',
        code: 'METADATA_TOO_LARGE',
      });
    }
  } catch (e) {
    errors.push({
      field: 'metadata',
      message: 'Metadata contains invalid JSON values',
      code: 'METADATA_INVALID_JSON',
    });
  }

  return errors;
}

/**
 * Validate complete dataset for creation
 */
export function validateDatasetCreation(data: {
  name: string;
  sourceId: string | null;
  partitions: PartitionField[];
  metadata: MetadataField[];
}): {
  isValid: boolean;
  errors: DatasetValidationError[];
} {
  const errors: DatasetValidationError[] = [];

  errors.push(...validateDatasetName(data.name));
  errors.push(...validateSource(data.sourceId));
  errors.push(...validatePartitions(data.partitions));
  errors.push(...validateMetadata(data.metadata));

  return {
    isValid: errors.length === 0,
    errors,
  };
}
